import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  ArrowLeft, Building2, KeyRound, Lock, Loader2, CheckCircle2,
  AlertCircle, Mail, Eye, EyeOff,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const rules = [
  { id: 'len', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { id: 'upper', label: 'One uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { id: 'lower', label: 'One lowercase letter', test: (p) => /[a-z]/.test(p) },
  { id: 'digit', label: 'One digit', test: (p) => /\d/.test(p) },
  { id: 'special', label: 'One special character', test: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function CompanyActivation() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [info, setInfo] = useState(null);
  const [loadingInfo, setLoadingInfo] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [form, setForm] = useState({ email: params.get('email') || '', otp: '', password: '', confirm: '' });

  useEffect(() => {
    const token = params.get('token');
    if (!token) { setLoadingInfo(false); return; }
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/company-onboarding/lookup-token?token=${encodeURIComponent(token)}`);
        const data = await res.json();
        if (res.ok) {
          setInfo(data);
          setForm((f) => ({ ...f, email: data.email || f.email }));
        } else {
          setInfo({ error: data.detail || 'Invalid activation link' });
        }
      } catch {
        setInfo({ error: 'Network error' });
      } finally { setLoadingInfo(false); }
    })();
  }, [params]);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const allRules = rules.map((r) => ({ ...r, ok: r.test(form.password) }));
  const passwordOk = allRules.every((r) => r.ok);
  const matchOk = form.password.length > 0 && form.password === form.confirm;

  const activate = async () => {
    if (!form.email || !form.otp || !form.password) { toast.error('Please fill all fields'); return; }
    if (!passwordOk) { toast.error('Password does not meet complexity requirements'); return; }
    if (!matchOk) { toast.error('Passwords do not match'); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/company-onboarding/activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: form.email, otp: form.otp, password: form.password }),
      });
      const data = await res.json();
      if (res.ok) {
        setDone(true);
        toast.success('Account activated!');
      } else {
        toast.error(data.detail || 'Activation failed');
      }
    } catch {
      toast.error('Network error');
    } finally { setSubmitting(false); }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-lg" data-testid="activation-success-card">
          <CardContent className="pt-8 pb-8 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-8 h-8 text-emerald-600" />
            </div>
            <h2 className="text-2xl font-bold text-slate-800 mb-2">Welcome to BodyCorp365!</h2>
            <p className="text-slate-600 mb-6">Your Super Agent account is now active. Please log in to continue.</p>
            <Button onClick={() => navigate('/login')} className="bg-indigo-600 hover:bg-indigo-700 w-full" data-testid="goto-login-btn">Go to Login</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-white via-sky-50 to-emerald-50">
      <header className="p-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-slate-700 hover:text-indigo-600">
            <ArrowLeft className="w-5 h-5" /><span>Home</span>
          </Link>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-indigo-600 flex items-center justify-center">
              <Building2 className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-slate-800">BodyCorp365</span>
          </div>
        </div>
      </header>

      <main className="flex justify-center p-4 pb-12">
        <div className="w-full max-w-md">
          <Card className="bg-white border-slate-200 shadow-lg">
            <CardHeader>
              <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center mb-3">
                <KeyRound className="w-6 h-6 text-emerald-600" />
              </div>
              <CardTitle className="text-slate-800">Activate Your Account</CardTitle>
              <CardDescription>
                {info?.company?.company_name ? (
                  <>Activate <strong>{info.company.company_name}</strong> ({info.company.company_id})</>
                ) : (
                  <>Enter the OTP you received by email and set your password.</>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {loadingInfo && (
                <div className="flex items-center justify-center py-4 text-slate-500"><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying link…</div>
              )}
              {info?.error && (
                <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 mt-0.5" />{info.error}
                </div>
              )}
              {info?.already_active && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  This account is already active. Please log in instead.
                </div>
              )}
              {info?.expired && !info?.error && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  Your activation link has expired. Please contact BodyCorp365 to request a fresh activation email.
                </div>
              )}

              <div className="space-y-2">
                <Label>Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input value={form.email} onChange={set('email')} className="pl-10" placeholder="you@company.com" data-testid="activation-email-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>One-Time Password (OTP)</Label>
                <div className="relative">
                  <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input value={form.otp} onChange={set('otp')} className="pl-10 tracking-widest font-mono" placeholder="8-digit OTP" data-testid="activation-otp-input" />
                </div>
              </div>

              <div className="space-y-2">
                <Label>New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type={showPw ? 'text' : 'password'} value={form.password} onChange={set('password')} className="pl-10 pr-10" data-testid="activation-password-input" />
                  <button type="button" onClick={() => setShowPw((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500" data-testid="toggle-show-password">
                    {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <ul className="text-xs space-y-0.5 mt-2">
                  {allRules.map((r) => (
                    <li key={r.id} className={r.ok ? 'text-emerald-600' : 'text-slate-400'} data-testid={`req-${r.id}`}>
                      {r.ok ? '✓' : '○'} {r.label}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="space-y-2">
                <Label>Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input type={showPw ? 'text' : 'password'} value={form.confirm} onChange={set('confirm')} className="pl-10" data-testid="activation-confirm-input" />
                </div>
                {form.confirm.length > 0 && (
                  <p className={`text-xs ${matchOk ? 'text-emerald-600' : 'text-red-600'}`} data-testid="match-indicator">
                    {matchOk ? '✓ Passwords match' : '✗ Passwords do not match'}
                  </p>
                )}
              </div>

              <Button onClick={activate} disabled={submitting || info?.already_active || info?.expired || !!info?.error} className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="activate-account-btn">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Activating…</> : 'Activate My Account'}
              </Button>

              <p className="text-center text-sm text-slate-600">
                Already activated?{' '}
                <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">Log in</Link>
              </p>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
